<template>
  <div>
    <h5 class=" mb-2" style="color: #0055A6; font-weight: bolder;">{{ $t("appGroupManagement") }}</h5>
  </div>
  <div class="d-flex justify-content-end gap-3 mb-3">
    <Button v-if="!templatePolicy" @click="onImport" class="custom-icon-button">
      <i class="ag-icon ag-icon_import"></i>
      <span class="label-text">{{ $t("import") }}</span>
    </Button>
    <AppGroupImportModal ref="importModal" @imported="getList" />


    <Button @click="onAddNew" class="custom-icon-button">
      <i class="ag-icon ag-icon_add_icon"></i>
      <span class="label-text">{{ $t("addNewAppgroup") }}</span>
    </Button>
  </div>

  <div class="div-wrapper app-group-management">
    <div class="input-class d-flex align-items-center gap-2 mt-3 mb-3 px-2 ">
      <div class="search-wrapper position-relative">
        <InputText v-model="searchQuery" :placeholder="$t('search')" />
        <i class="pi pi-search search-icon" />
      </div>

      <div class="d-flex align-items-center gap-1">
        <button class="btn btn-link p-0" @click="clearSearch" style="text-decoration: none;">
          <i style="color: #5d5f62;" class="pi pi-times-circle"></i>
          <strong class="clear-text">{{ $t("clear") }}</strong>
        </button>
      </div>

      <span class="mx-1" style="color: #CCCCCC;">|</span>

      <span class="search-text">{{ $t("filterBy") }}</span>
      <MultiSelect ref="discoverySelect" v-model="selectedDiscoveryMode" :options="realOptions" optionLabel="label"
        optionValue="value" :placeholder="$t('discoveryMode')" :maxSelectedLabels="3" :showToggleAll="false" appendTo="self">
        <template #header="{ value, options }">
          <div class="p-multiselect-header d-flex align-items-center justify-content-between w-100">
            <div class="d-flex align-items-center">
              <Checkbox :binary="true" :modelValue="isAllSelected(value, options)"
                @update:modelValue="(checked) => toggleAll(checked, options)" />
              <span class="ms-2" style="color:#5d5f62; font-size:0.875rem;">{{ $t('selectAll') }}</span>
            </div>
            <button type="button" class="p-link p-multiselect-close p-0" @click="discoverySelect?.hide?.()">
              <i class="pi pi-times" />
            </button>
          </div>
        </template>
      </MultiSelect>
      <div class="flex-grow-1"></div>
        <div style="color:#3C73AA; font-size: 14px; margin-left: auto;">
            {{ $t('total') }} <span style="font-weight:700;">{{ filteredAppGroupsRows.length }} {{ $t('appGroups') }}</span>
        </div>

    </div>
    <ag-bulk-action-toolbar v-if="selectedIds.length > 0" :realOptions="realOptions" v-model:bulk="bulk" :disabled-apply="selectedIds.length===0" :disabled-delete="deleteDisabled" :disabled-reset="resetDisabled" @apply="bulkApply" @delete="bulkDelete" @reset="bulkReset" :selection-count="selectedIds.length" @applyNote="applyNote" @cancelSection="clearBulkSelection" />
    <ag-card
      :key="tableKey"
      @return-checked-rows="onSelectionChange"
      :module="{ ...module, component: { ...module.component, options: { ...module.component.options, rows: filteredAppGroupsRows } } }"
      @deleteClick="deleteAppGroup"></ag-card>
    <EditPanel :show-enabled="true" v-model="showPanel" :title="formTitle" :form-component="formFileName"
      :form="formData" @save="saveHandler" @cancel="cancelHandler" form-type="appGroup" :saving="isSaving" />
  </div>
</template>

<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import AppManagement from "@/helpers/pages/AppManagement";
import AgCard from "@/components/ag-card/index.vue";
import EditPanel from '@/components/ag-edit-panel/index.vue'
import AppGroupForm from '@/components/ag-edit-panel/forms/app-group-form.vue'
import Swal from 'sweetalert2';
import { GeneralActionTypes } from "@/store/modules/general/action-types";
import { useStore } from "@/store";
import { useRoute, useRouter } from "vue-router";
import AgLoading from "@/components/ag-loading/index.vue";
import AppGroupImportModal from "@/components/ag-edit-panel/forms/app-group-import-modal.vue";
import { useI18n } from 'vue-i18n';
import Checkbox from 'primevue/checkbox'
import AgBulkActionToolbar from '@/components/ag-bulk-action-toolbar/index.vue'
import { AppGroupTypes } from "@/helpers/enums/AppGroupTypes";
const { t } = useI18n();
const loading = ref(false);
const isSaving = ref(false);
const store = useStore();
const router = useRouter();

const searchQuery = ref('')
const selectedDiscoveryMode = ref('');
const discoverySelect = ref(null);
const showPanel = ref(false)
let formTitle = ref(t('addNewAppGroup'))
let formFileName = ref(AppGroupForm)
const formData = ref({
  id: 0,
  name: "",
  description: "",
  memorySecurity: 0,
  inheritance: 0,
  subsystemsInDiscoveryMode: 0,
  audit: 1,
  companyId: ""
})
const discoveryOptions = computed(() => store.state.general.enums['DiscoveryMode'] || [])
const realOptions = computed(() => discoveryOptions.value.filter(opt => opt.label !== 'Enable All' && String(opt.label).toLowerCase() !== 'none'))
const company = computed(() => store.getters.company);
const templatePolicy = ref(false);
const group = ref(null);
const site = ref({});
const template = ref('');
const templateId = ref(0);
const currentRole = ref('');

function clearSearch() {
  searchQuery.value = '';
  selectedDiscoveryMode.value = [];
  if (module.value?.component?.options?.sortable) {
    module.value.component.options.sortable.order = '';
    module.value.component.options.sortable.sort = '';
  }
}

function updateRowsFromStore() {
  if (module.value?.component?.options) {
    module.value.component.options.rows = appGroupList.value;
  }
}

let module = ref(AppManagement);

const appGroupList = computed(() => store.state.general.appGroups);

watch(appGroupList, () => {
  updateRowsFromStore();
});
const getList = async () => {
  try {
    await store.dispatch(GeneralActionTypes.GET_APPGROUPS);
  } finally {
    module.value.component.options.isLoading = false;
  }
};
const getListTemplate = async () => {
  const template = localStorage.getItem("agms_template");
  const templateId = template ? JSON.parse(template)?.mainid : null;
  try {
    await store.dispatch(GeneralActionTypes.GET_APPGROUPS_TEMPLATE, templateId);
  } finally {
    module.value.component.options.isLoading = false;
  }
};
const fetchEnumAndStore = async (name) => {
  await store.dispatch(GeneralActionTypes.GET_ENUM_BY_NAME, name);
};
const filteredAppGroupsRows = computed(() => {
  const query = searchQuery.value.toLowerCase();
  const selectedModes = selectedDiscoveryMode.value;
  const rows = module.value.component.options.rows || [];

  return rows.filter(row => {
    const matchesSearch = !row.name || row.name.toLowerCase().includes(query);

    const matchesAppList = row.appGroupProcess?.some(process => {
      const fileMatch = process.fileName?.toLowerCase().includes(query);
      const folderName = process.folderMapId != null ? getFolderNameById(process.folderMapId) : "";
      const folderMatch = (folderName || "").toLowerCase().includes(query);
      return fileMatch || folderMatch;
    });

    const matchesDiscoveryMode =
      !selectedModes || selectedModes.length === 0 || selectedModes.some(mode =>
        (row.subsystemsInDiscoveryMode & mode) === mode
      );

    return (matchesSearch || matchesAppList) && matchesDiscoveryMode;
  });
})

function getFolderNameById(value) {
  if (value === undefined || value === null) {
    return "";
  }
  const folderMaps = store.state.general.folderMaps;
  const folder = folderMaps.find(item => item.value === value);
  return folder ? folder.label : "";
}

const getFolderMaps = async () => {
  await store.dispatch(GeneralActionTypes.GET_FOLDER_MAPS);
}

const getInheritanceInfo = async () => {
  const group = localStorage.getItem("agms_group");
  const groupId = group ? JSON.parse(group)?.mainid : null;
  await store.dispatch(GeneralActionTypes.GET_APPGROUP_INHERITANCE_INFO, { groupId });
};

onMounted(() => {
  getFolderMaps();
  const routeName = useRoute().name;
  if (routeName == 'templateAppGroupManagement') {
    const templateStr = localStorage.getItem("agms_template");
    const mainId = templateStr ? JSON.parse(templateStr)?.mainid : null;
    templateId.value = mainId;
    currentRole.value = templateStr ? JSON.parse(templateStr)?.template : null;
    templatePolicy.value = true;
    getListTemplate();
  } else {
    getList();
    getInheritanceInfo();
  }
  group.value = JSON.parse(localStorage.getItem("agms_group")) || {};
  site.value = JSON.parse(localStorage.getItem("agms_site")) || {};
  updateRowsFromStore();
  fetchEnumAndStore("Inheritance");
  fetchEnumAndStore("MemorySecurity");
  fetchEnumAndStore("DiscoveryMode");
  fetchEnumAndStore("AppGroupAudit");
  fetchEnumAndStore("AppGroupType");

});
const importModal = ref(null);
const onImport = () => {

  if (importModal.value?.open) {
    importModal.value.open();
  } else {
    console.error("Modal ref not found!");
  }
};


const onAddNew = () => {
  formTitle.value = t('addNewAppGroup');
  formFileName.value = AppGroupForm;
  showPanel.value = true
};

const extractErrorMessage = (errors) => {
  if (!errors) return null;
  if (typeof errors === "string") return errors;
  if (Array.isArray(errors)) return errors.flat().join("<br>");
  if (typeof errors === "object") return Object.values(errors).flat().join("<br>");
  return String(errors);
};

const handleBulkResponse = (response, actionLabel) => {
  try {
    const data = response ?? {};
    const payload = data.entityData ?? data.data ?? data;
    const results = payload?.results ?? payload?.entityData?.results ?? [];

    const totalRequested = payload?.totalRequested ?? results.length ?? 0;
    const successCount = payload?.successCount ?? results.filter(r => r?.success === true).length ?? 0;
    const failedCount = payload?.failedCount ?? results.filter(r => r?.success === false).length ?? 0;
    let successMsg = "";
    if(actionLabel == t('bulkUpdate')) {
      successMsg = t('successUpdate');
    }else if(actionLabel == t('bulkDelete')) {
      successMsg = t('successDelete');
    }else if(actionLabel == t('bulkReset')) {
      successMsg = t('successReset');
    }else if(actionLabel == t('bulkApplyNote')) {
      successMsg = t('successApplyNote');
    }

    if (failedCount > 0) {
      const errorItems = results
        .filter(r => r && r.success === false)
        .map(r => {
          const name = r.appGroupName || r.name || r.appGroupId || "Unknown";
          const msg = r.errorMessage || r.message || "Unknown error";
          return `<li>${name}: ${msg}</li>`;
        })
        .join("");

      const successItems = results
        .filter(r => r && r.success === true)
        .map(r => {
          const name = r.appGroupName || r.name || r.appGroupId || "Unknown";
          return `<li>${name}: ${successMsg}</li>`;
        })
        .join("");

      const title = failedCount === totalRequested
        ? t('error') || 'Error'
        : t('warning') || 'Warning';
      const icon = failedCount === totalRequested ? 'error' : 'warning';
      const headerLine = `<div style="margin-bottom:6px;">${actionLabel || t('bulkAction')} completed: ${successCount} succeeded, ${failedCount} failed.</div>`;

      const successSection = successCount > 0
        ? `<div style="margin-top:8px; margin-bottom:4px; font-weight:600;">${t('successful') || 'Successful'} (${successCount})</div>
           <ul style="text-align:left; margin:0; padding-left:18px; max-height:200px; overflow:auto;">${successItems}</ul>`
        : '';

      const errorSection =
        `<div style="margin-top:12px; margin-bottom:4px; font-weight:600;">${t('failed') || 'Failed'} (${failedCount})</div>
         <ul style="text-align:left; margin:0; padding-left:18px; max-height:200px; overflow:auto;">${errorItems}</ul>`;

      Swal.fire({
        title,
        html: `${headerLine}${successSection}${errorSection}`,
        icon,
        width: 700
      });
    } else if (successCount > 0) {
      const successItems = results
        .filter(r => r && r.success === true)
        .map(r => {
          const name = r.appGroupName || r.name || r.appGroupId || "Unknown";
          return `<li>${name}: ${successMsg}</li>`;
        })
        .join("");
        const successSection = successCount > 0
        ? `<div style="margin-top:8px; margin-bottom:4px; font-weight:600;">${t('successful') || 'Successful'} (${successCount})</div>
           <ul style="text-align:left; margin:0; padding-left:18px; max-height:200px; overflow:auto;">${successItems}</ul>`
        : '';
      Swal.fire({ title: t('success'), html: successSection, icon: 'success', showConfirmButton: true });
    }
  } catch (e) {
    console.warn('Unexpected bulk response format', e, response);
  }
};

const saveHandler = async (dataFromForm) => {
  if (isSaving.value) return;
  isSaving.value = true;
  const group = localStorage.getItem("agms_group");
  const groupId = group ? JSON.parse(group)?.mainid : null;
  let body = {
    id: 0,
    name: dataFromForm.name,
    description: dataFromForm.description,
    memorySecurity: dataFromForm.memorySecurity,
    inheritance: dataFromForm.inheritance,
    subsystemsInDiscoveryMode: dataFromForm.subsystemsInDiscoveryMode,
    audit: dataFromForm.audit,
    companyId: company.value.companyGuid,
    groupId: templatePolicy.value ? null : groupId,
    enabled: dataFromForm.enabled ? dataFromForm.enabled : false,
    appGroupType: dataFromForm.appGroupType,
    expertNotes: dataFromForm.expertNotes,
    childProcess: dataFromForm.childProcess,
    templateId: templatePolicy.value ? templateId.value : null,
  }
  try {
    const response = templatePolicy.value ? await store.dispatch(
      GeneralActionTypes.CREATE_OR_UPDATE_APPGROUP_TEMPLATE,
      body
    ) : await store.dispatch(
      GeneralActionTypes.CREATE_OR_UPDATE_APPGROUP,
      body
    );
    loading.value = true;
    if (response.isSuccess) {
      loading.value = false;
    }
    const appGroupId = response.entityData;
    if (templatePolicy.value) {
      getListTemplate();
    } else {
      getList();
    }
    showPanel.value = false;
    router.push({
      path: templatePolicy.value ? `/administration/app-group-management/detail-role` : `/administration/app-group-management/detail`,
      query: { id: appGroupId }
    });
  } catch (err) {
    const serverData = err?.response?.data ?? err?.data ?? null;
    let msg = "";

    if (serverData?.errors) {
      msg = extractErrorMessage(serverData.errors);
    } else if (serverData?.title) {
      msg = serverData.title;
    } else if (err?.message) {
      msg = err.message;
    } else {
      msg = JSON.stringify(err);
    }

    return Swal.fire({ title: t('error'), html: msg || t('unexpectedError'), icon: "error" });
  } finally {
    isSaving.value = false;
  }
}

async function deleteAppGroup(rowData) {
  const result = await Swal.fire({
    title: t('areYouSure'),
    icon: "warning",
    text: t('deleteAppGroupConfirm'),
    showCancelButton: true,
    confirmButtonText: t('delete'),
    cancelButtonText: t('cancel'),
  });
  if (result.isConfirmed) {
    try {
      const data = templatePolicy.value ? { ...rowData, templateId: templateId.value } : rowData.id;
      const response = templatePolicy.value
        ? await store.dispatch(GeneralActionTypes.DELETE_APPGROUP_TEMPLATE, data)
        : await store.dispatch(GeneralActionTypes.DELETE_APPGROUP, data);
      if (response.isSuccess) {
        Swal.fire(t('deleted'), t('appGroupDeletedSuccess'), "success");
        if (templatePolicy.value) {
          getListTemplate();
        } else {
          getList();
        }
      } else {
        Swal.fire(t('error'), response.data?.message || t('deleteFailed'), "error");
      }
    } catch (err) {
      console.error(err);
      Swal.fire(t('error'), t('deleteError'), "error");
    }
  }
};

const isAllSelected = (value, options) => {
  const selected = Array.isArray(value) ? value : [];
  return options && selected.length === options.length && options.length > 0;
};

const toggleAll = (checked, options) => {
  if (!Array.isArray(options)) return;
  selectedDiscoveryMode.value = checked ? options.map(o => o.value) : [];
};

const bulk = ref({
  enabled: null,
  audit: null, 
  discoveryModes: null,
  expertNotes: ''
});

function resetBulkSelections() {
  bulk.value = {
    enabled: null,
    audit: null,
    discoveryModes: null,
    expertNotes: ''
  };
}

const selectedIds = ref([]);

const nonDeletableNames = new Set([
  'Restricted (TPL)',
  'Allow List',
  'Prohibited',
  'Standard Containment',
  'Private Containment',
  'Power Apps',
]);

const deleteDisabled = computed(() => {
  if (selectedIds.value.length === 0) return true;

  const hasAnyDeletable = selectedIds.value.some(id => {
    const row = appGroupList.value.find(r => Number(r.id) === Number(id));
    
    if (!row) {
      return false;
    }
    
    const name = row.name;
    if (!name) return false;
  
    const isDeletable = !nonDeletableNames.has(name);
    return isDeletable;
  });
  
  return !hasAnyDeletable;
});

const resetDisabled = computed(() => {
  if (selectedIds.value.length === 0) return true;

  const allInheritedOrCustom = selectedIds.value.every(id => {
    const row = appGroupList.value.find(r => Number(r.id) === Number(id));
    
    if (!row) {
      return true;
    }
    
    return row.isInherited === true || row.appGroupType === AppGroupTypes.Custom;
  });
  
  return allInheritedOrCustom;
});

function onSelectionChange(ids) {
  selectedIds.value = Array.isArray(ids) ? ids : [];
  if (selectedIds.value.length === 0) {
    bulk.value = {
      enabled: null,
      audit: null,
      discoveryModes: null,
      expertNotes: ''
    };
  }
}

async function bulkDelete() {
  if (selectedIds.value.length === 0) return;
  try {
    module.value.component.options.isLoading = true;
    isSaving.value = true;
    const payload = {
      appGroupIds: selectedIds.value,
      delete: true
    }
    const response = await store.dispatch(GeneralActionTypes.BULK_ACTION_APPGROUP, payload);
    handleBulkResponse(response, t('bulkDelete'));
  } catch (err) {
    console.error(err);
    module.value.component.options.isLoading = false;
    Swal.fire({ title: t('error'), text: t('bulkDeleteFailed'), icon: 'error' });
  } finally {
    module.value.component.options.isLoading = false;
    isSaving.value = false;
    selectedIds.value = [];
    tableKey.value++;
    if (templatePolicy.value) await getListTemplate(); else await getList();
    resetBulkSelections();
  }
}
async function applyNote(noteValue) {
  if (selectedIds.value.length === 0) return;
  try{
    const payload = {
      appGroupIds: selectedIds.value,
      expertNotes: noteValue || ''
    }
    module.value.component.options.isLoading = true;
    const response = await store.dispatch(GeneralActionTypes.BULK_ACTION_APPGROUP, payload);
    handleBulkResponse(response, t('bulkApplyNote'));
  }catch(err){
    console.error(err);
    module.value.component.options.isLoading = false;
    Swal.fire({ title: t('error'), text: t('bulkApplyNoteFailed'), icon: 'error' });
  }finally{
    module.value.component.options.isLoading = false;
    isSaving.value = false;
    selectedIds.value = [];
    tableKey.value++;
    if (templatePolicy.value) await getListTemplate(); else await getList();
    resetBulkSelections();
  }
}

async function bulkApply() {
  if (selectedIds.value.length === 0) return;
  try {
    isSaving.value = true;
    const sumDiscovery = (arr) => (Array.isArray(arr) ? arr.map(Number).reduce((acc, v) => acc | v, 0) : 0);

    const payload = {
      appGroupIds: selectedIds.value,
      delete: false
    };
    if(bulk.value.enabled !== null){
      payload.enabled = bulk.value.enabled;
    }

    if (bulk.value.audit !== null && bulk.value.audit !== undefined) {
      payload.auditMode = bulk.value.audit;
    }

    const discoveryMask = sumDiscovery(bulk.value.discoveryModes);
    if (discoveryMask > 0) {
      payload.discoveryMode = discoveryMask;
    }
    module.value.component.options.isLoading = true;

    const response = await store.dispatch(GeneralActionTypes.BULK_ACTION_APPGROUP, payload);
    handleBulkResponse(response, t('bulkUpdate'));
  } catch (err) {
    module.value.component.options.isLoading = false;
    Swal.fire({ title: t('error'), text: t('bulkApplyFailed'), icon: 'error' });
  } finally {
    module.value.component.options.isLoading = false;
    isSaving.value = false;
    selectedIds.value = [];
    tableKey.value++;
    if (templatePolicy.value) await getListTemplate(); else await getList();
    resetBulkSelections();
  }
}

async function bulkReset() {
  module.value.component.options.isLoading = true;
  if (selectedIds.value.length === 0) return;
  try {
    isSaving.value = true;
    const payload = {
      appGroupIds: selectedIds.value,
      groupId:group.value.mainid,
      resetToDefault: true
    }
    const response = await store.dispatch(GeneralActionTypes.BULK_ACTION_APPGROUP, payload);
    handleBulkResponse(response, t('bulkReset'));
  } catch (err) {
    console.error(err);
    module.value.component.options.isLoading = false;
    Swal.fire({ title: t('error'), text: t('bulkResetFailed'), icon: 'error' });
  } finally {
    module.value.component.options.isLoading = false;
    isSaving.value = false;
    selectedIds.value = [];
    tableKey.value++;
    if (templatePolicy.value) await getListTemplate(); else await getList();
    resetBulkSelections();
  }
}

const tableKey = ref(0);

function clearBulkSelection() {
  selectedIds.value = [];
  
  if (appGroupList.value && Array.isArray(appGroupList.value)) {
    appGroupList.value.forEach(row => {
      if (row) row.checked = false;
    });
  }
  
  tableKey.value++;
  resetBulkSelections();
}
</script>

<style scoped>
.div-wrapper {
  border-top: 1px solid #D2D9E2;
  border-left: 1px solid #D2D9E2;
  border-right: 1px solid #D2D9E2;
}

.custom-icon-button {
  display: flex;
  align-items: center;
  gap: 8px;
  background-color: transparent;
  border: none;
  box-shadow: none;
  color: #2c5f89;
  font-weight: bold;
  text-transform: uppercase;
  padding: 0;
  font-size: 0.8rem;
}

.custom-icon-button:hover {
  opacity: 0.8;
  cursor: pointer;
}

.icon-circle {
  background-color: #2c5f89;
  color: white;
  border-radius: 50%;
  padding: 8px;
  font-size: 0.7rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.search-wrapper {
  position: relative;
}

.search-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
  padding-left: 5px;
  border-left: 1px solid #D2D9E2;
}

.p-inputtext {
  padding: 0.25rem 0.25rem;
  font-size: 0.875rem;
  background: transparent;
}

::v-deep(.p-multiselect .p-multiselect-label) {
  font-size: 0.875rem;
  padding: 0.25rem 0.25rem !important;
}

.p-multiselect {
  background: transparent !important;
}

.clear-text {
  color: #5d5f62;
  font-size: 0.875rem;
  margin-left: 5px;
}

.search-text {
  color: #5d5f62;
  font-size: 0.875rem;
}

.label-text {
  color: #3C73AA;
}

::v-deep(.p-multiselect .p-multiselect-header .p-multiselect-close) {
  color: #6c757d;
}

::v-deep(.p-multiselect-panel .p-checkbox .p-checkbox-box) {
  width: 18px !important;
  height: 18px !important;
}

::v-deep(.p-multiselect-panel .p-checkbox .p-checkbox-box .p-checkbox-icon) {
  font-size: 12px;
}

::v-deep(.p-multiselect-panel .p-multiselect-items .p-multiselect-item) {
  padding: 0.5rem 1.5rem;
}

::v-deep(#ag-table thead th > div) {
  color: #5D5F62;
  font-weight: 700;
}

::v-deep(#ag-table thead th > div span),
::v-deep(#ag-table thead th > div .ag-tooltip) {
  color: inherit;
  font-weight: inherit;
}

.app-group-management ::v-deep(#ag-table:has(thead th.checkbox-th) thead th.checkbox-th + th),
.app-group-management ::v-deep(#ag-table:has(tbody td .row-checkbox) tbody td:has(.row-checkbox) + td) {
  border-left: 0 !important;
}
.app-group-management :deep(.ag-card_body) {
  border: none !important ;
}
::v-deep(.p-dropdown .p-dropdown-clear-icon) {
    width: 0.7rem;
    margin-right: -0.5rem;
}
</style>
